/**
 * Notices for the three people in the chain (spec §8, subsystem 5).
 *
 * Cross-cutting by nature, which is why it comes last: the states have to
 * settle before it is worth telling anyone about them. `statusWatch.js`
 * already answers "what moved since I last looked" for the staff console;
 * this is the other half — turning a state into something addressed to
 * somebody, worded for them, with somewhere to go.
 *
 * One rule throughout: a notice either asks for something (`action`), reports
 * a number worth looking at (`warn`), or says what happened (`note`). Only
 * the first is allowed to be noisy.
 *
 * Pure. The caller fetches and passes in whatever that audience can see.
 */

import { isVendor } from './rosterLine';
import { withReturnTo } from './returnTo';
import { rollupRows } from './spendRollup';
import { runState, RUN_STATES, eligibleForRun, strandedByWithdrawal } from './paymentRun';

export const AUDIENCE = {
  BUSINESS: 'business',
  CREATOR: 'creator',
  FINANCE: 'finance',
};

export const NOTICE = {
  NEEDS_REVIEW: 'needs_review',
  OVER_INTERNAL_COST: 'over_internal_cost',
  ONBOARDING_INCOMPLETE: 'onboarding_incomplete',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PAID: 'paid',
  NO_PAYOUT: 'no_payout',
  READY_TO_PAY: 'ready_to_pay',
  PAYOUT_NOT_READY: 'payout_not_ready',
  STRANDED_BY_WITHDRAWAL: 'stranded_by_withdrawal',
  AWAITING_REFERENCE: 'awaiting_reference',
};

/** Separate from the console's watch key: a different thing is remembered. */
export const SEEN_KEY = 'invoice_notices_seen';

/** Where a notice sends each role. Named once so a route change is one edit. */
const CONSOLE_PATH = '/planner/invoices';
const WORKSPACE_PATH = '/planner/invoices/workspace';
const CREATOR_INVOICES_PATH = '/influencer/invoices';
const PAYOUT_PATH = '/influencer/payout-settings';

const SEVERITY_RANK = { action: 0, warn: 1, note: 2 };

/** How each kind reads in a digest, and how it counts. */
const PHRASES = {
  [NOTICE.NEEDS_REVIEW]: ['invoice to review', 'invoices to review'],
  [NOTICE.OVER_INTERNAL_COST]: [
    'campaign over its agreed cost',
    'campaigns over their agreed cost',
  ],
  [NOTICE.ONBOARDING_INCOMPLETE]: ['creator still to onboard', 'creators still to onboard'],
  [NOTICE.APPROVED]: ['invoice approved', 'invoices approved'],
  [NOTICE.REJECTED]: ['invoice sent back', 'invoices sent back'],
  [NOTICE.PAID]: ['payment received', 'payments received'],
  [NOTICE.NO_PAYOUT]: ['payout detail to add', 'payout details to add'],
  [NOTICE.READY_TO_PAY]: ['invoice ready to pay', 'invoices ready to pay'],
  [NOTICE.PAYOUT_NOT_READY]: ['approved invoice that cannot be paid',
    'approved invoices that cannot be paid'],
  [NOTICE.STRANDED_BY_WITHDRAWAL]: ['approved invoice on a withdrawn line',
    'approved invoices on withdrawn lines'],
  [NOTICE.AWAITING_REFERENCE]: ['run awaiting its reference', 'runs awaiting their reference'],
};

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

const text = (value) => (isBlank(value) ? '' : String(value).trim());

/** Whoever the invoice is for. */
const whoOf = (invoice) =>
  text(invoice.vendor_name) || text(invoice.influencer_name) || 'A creator';

const whatOf = (invoice) => text(invoice.campaign_name) || 'a campaign';

/** A roster line's creator, whichever field the record carries it in. */
const nameOfLine = (line) => text(line.influencer_name) || text(line.name) || 'A creator';

const notice = (fields) => ({ at: '', ...fields });

/** Stable across reloads, so a seen notice stays seen. */
export function noticeKey(item) {
  const record = item || {};
  const subject =
    record.invoice_id ?? record.campaign_id ?? record.run_id ?? record.line_id ?? '';
  return `${record.audience}:${record.kind}:${subject}`;
}

function businessNotices({ invoices, campaigns, lines }) {
  const needsReview = invoices
    .filter((invoice) => invoice.status === 'submitted')
    .map((invoice) =>
      notice({
        audience: AUDIENCE.BUSINESS,
        kind: NOTICE.NEEDS_REVIEW,
        severity: 'action',
        title: `${whoOf(invoice)} submitted an invoice for ${whatOf(invoice)}`,
        detail: 'Review it before it can go to finance.',
        href: CONSOLE_PATH,
        invoice_id: invoice.id,
        campaign_id: invoice.campaign_id,
        at: text(invoice.submitted_at),
      }),
    );

  const overrun = rollupRows(campaigns, invoices)
    .filter((row) => row.over)
    .map((row) =>
      notice({
        audience: AUDIENCE.BUSINESS,
        kind: NOTICE.OVER_INTERNAL_COST,
        severity: 'warn',
        title: `${row.name || 'A campaign'} has invoiced past its agreed internal cost`,
        detail: `${row.invoiced} invoiced against ${row.internal_cost} agreed.`,
        href: `${WORKSPACE_PATH}?campaign_id=${row.campaign_id}`,
        campaign_id: row.campaign_id,
      }),
    );

  // A locked line with no payout details is a creator who has been promised a
  // price and not finished onboarding. The line's internal POC owns the chase.
  const waiting = lines
    .filter((line) => line.price_locked && !line.payout_details_on_file && !isVendor(line))
    .map((line) =>
      notice({
        audience: AUDIENCE.BUSINESS,
        kind: NOTICE.ONBOARDING_INCOMPLETE,
        severity: 'note',
        // A line straight from `linePayload` carries `name`; one refetched
        // from the API carries `influencer_name`. A chase that names nobody
        // is not a chase, so read both.
        title: `${nameOfLine(line)} has a locked price but no payout details`,
        detail: 'They cannot invoice until they finish account setup.',
        href: line.campaign_id
          ? `${WORKSPACE_PATH}?campaign_id=${line.campaign_id}`
          : WORKSPACE_PATH,
        line_id: line.id,
        campaign_id: line.campaign_id,
      }),
    );

  return [...needsReview, ...overrun, ...waiting];
}

function creatorNotices({ invoices, payoutOnFile }) {
  const byStatus = {
    approved: {
      kind: NOTICE.APPROVED,
      severity: 'note',
      title: (invoice) => `Your invoice for ${whatOf(invoice)} was approved`,
      detail: 'It is with finance for payment.',
    },
    rejected: {
      kind: NOTICE.REJECTED,
      severity: 'action',
      title: (invoice) => `Your invoice for ${whatOf(invoice)} was sent back`,
      detail: 'Check the remarks and submit it again.',
    },
    paid: {
      kind: NOTICE.PAID,
      severity: 'note',
      title: (invoice) => `You were paid for ${whatOf(invoice)}`,
      detail: 'The payment reference is on the invoice.',
    },
  };

  const fromInvoices = invoices
    .filter((invoice) => byStatus[invoice.status])
    .map((invoice) => {
      const rule = byStatus[invoice.status];
      return notice({
        audience: AUDIENCE.CREATOR,
        kind: rule.kind,
        severity: rule.severity,
        title: rule.title(invoice),
        detail: rule.detail,
        href: CREATOR_INVOICES_PATH,
        invoice_id: invoice.id,
        campaign_id: invoice.campaign_id,
        at: text(invoice.submitted_at),
      });
    });

  // `undefined` means we have not asked yet; only a definite false is a chase.
  const payout =
    payoutOnFile === false
      ? [
          notice({
            audience: AUDIENCE.CREATOR,
            kind: NOTICE.NO_PAYOUT,
            severity: 'action',
            title: 'Add your payout details',
            detail: 'An invoice cannot be submitted until your bank details are on file.',
            // The page built for exactly this, carrying a way back to the
            // invoice list — the same destination the account checklist uses.
            href: withReturnTo(PAYOUT_PATH, CREATOR_INVOICES_PATH),
          }),
        ]
      : [];

  return [...fromInvoices, ...payout];
}

function financeNotices({ invoices, runs }) {
  const ready = eligibleForRun(invoices).map((invoice) =>
    notice({
      audience: AUDIENCE.FINANCE,
      kind: NOTICE.READY_TO_PAY,
      severity: 'action',
      title: `${whoOf(invoice)} is approved and waiting for payment`,
      detail: `${whatOf(invoice)}. Add it to a payment run.`,
      href: `${CONSOLE_PATH}?status=approved`,
      invoice_id: invoice.id,
      campaign_id: invoice.campaign_id,
      at: text(invoice.submitted_at),
    }),
  );

  // Item 6. `payout_ready` is explicit-false only: an older API build sends
  // nothing, and reading that as incomplete would flag the whole console.
  // A vendor is invoiced on their behalf and has no record to fill.
  const blocked = invoices
    .filter(
      (invoice) =>
        invoice.status === 'approved' &&
        !text(invoice.vendor_name) &&
        ((invoice.staff || {}).payout_ready === false),
    )
    .map((invoice) =>
      notice({
        audience: AUDIENCE.FINANCE,
        kind: NOTICE.PAYOUT_NOT_READY,
        severity: 'warn',
        title: `${whoOf(invoice)} is approved but cannot be paid yet`,
        detail:
          `Their payout record for ${whatOf(invoice)} is incomplete, so a payment run `
          + 'will reject this invoice. Bank, PAN and — if they are MSME — the Udyam '
          + 'number all have to be on file.',
        href: `${CONSOLE_PATH}?status=approved`,
        invoice_id: invoice.id,
        campaign_id: invoice.campaign_id,
        at: text(invoice.submitted_at),
      }),
    );

  // Dropped from eligibleForRun so a run can be assembled at all. Said out
  // loud here, because an approved invoice is money someone is owed and
  // quietly vanishing from the picker is how it stops being chased.
  const stranded = strandedByWithdrawal(invoices).map((invoice) =>
    notice({
      audience: AUDIENCE.FINANCE,
      kind: NOTICE.STRANDED_BY_WITHDRAWAL,
      severity: 'warn',
      title: `${whoOf(invoice)} has an approved invoice no payment run can take`,
      // Names the actual route rather than the condition. Confirmed with the
      // backend on 29 Oct: re-adding the same creator reuses the existing
      // line, sets it back to approved and keeps its prices, after which the
      // invoice is eligible again.
      detail:
        `Their line on ${whatOf(invoice)} was withdrawn from the campaign, so this `
        + 'invoice is held out of payment runs. An invoice approved before the '
        + 'withdrawal is still owed: add the creator back to the campaign in the '
        + 'workspace and it becomes payable again, with its prices unchanged.',
      href: `${WORKSPACE_PATH}${invoice.campaign_id ? `?campaign_id=${invoice.campaign_id}` : ''}`,
      invoice_id: invoice.id,
      campaign_id: invoice.campaign_id,
      at: text(invoice.submitted_at),
    }),
  );

  const awaiting = runs
    .filter((run) => runState(run) === RUN_STATES.EXPORTED)
    .map((run) =>
      notice({
        audience: AUDIENCE.FINANCE,
        kind: NOTICE.AWAITING_REFERENCE,
        severity: 'action',
        title: `The payment run of ${text(run.date) || 'this period'} has a file but no reference`,
        detail: 'Mark its invoices paid once the bank confirms.',
        href: `${CONSOLE_PATH}?status=sent_for_payment`,
        run_id: run.id,
        at: text(run.exported_at),
      }),
    );

  return [...blocked, ...stranded, ...ready, ...awaiting];
}

const BY_AUDIENCE = {
  [AUDIENCE.BUSINESS]: businessNotices,
  [AUDIENCE.CREATOR]: creatorNotices,
  [AUDIENCE.FINANCE]: financeNotices,
};

/**
 * Everything this audience should be told, what needs doing first and the
 * most recent of those first.
 */
export function noticesFor(audience, context) {
  const build = BY_AUDIENCE[audience];
  if (!build || !context) return [];

  const list = build({
    invoices: (Array.isArray(context.invoices) ? context.invoices : []).filter(Boolean),
    campaigns: (Array.isArray(context.campaigns) ? context.campaigns : []).filter(Boolean),
    lines: (Array.isArray(context.lines) ? context.lines : []).filter(Boolean),
    runs: (Array.isArray(context.runs) ? context.runs : []).filter(Boolean),
    payoutOnFile: context.payoutOnFile,
  });

  return [...list].sort((a, b) => {
    const rank = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rank !== 0) return rank;
    // Most recent first; an undated notice sorts after dated ones.
    return String(b.at).localeCompare(String(a.at));
  });
}

/** Counted by kind, in the same order the notices themselves came in. */
export function groupNotices(notices) {
  const list = Array.isArray(notices) ? notices : [];

  return list.reduce((groups, item) => {
    const existing = groups.find((group) => group.kind === item.kind);
    if (existing) {
      return groups.map((group) =>
        group.kind === item.kind ? { ...group, count: group.count + 1 } : group,
      );
    }
    return [...groups, { kind: item.kind, severity: item.severity, count: 1 }];
  }, []);
}

/** One line for a header or a badge; null when there is nothing to say. */
export function digestOf(notices) {
  const groups = groupNotices(notices);
  if (groups.length === 0) return null;

  return groups
    .map((group) => {
      const [one, many] = PHRASES[group.kind] || ['notice', 'notices'];
      return `${group.count} ${group.count === 1 ? one : many}`;
    })
    .join(' · ');
}

/** The notices not in the seen list. */
export function unseenOf(notices, seen) {
  const list = Array.isArray(notices) ? notices : [];
  const keys = new Set(Array.isArray(seen) ? seen : []);
  return list.filter((item) => !keys.has(noticeKey(item)));
}

/** The seen list with these notices added. Never mutates. */
export function markNoticesSeen(seen, notices) {
  const existing = Array.isArray(seen) ? seen : [];
  const keys = (Array.isArray(notices) ? notices : []).map(noticeKey);
  return [...new Set([...existing, ...keys])];
}
